import React from "react";
import PropTypes from "prop-types";
import { injectStripe } from "react-stripe-elements";
import CardNumber from "components/v3/credit-card/number/CardNumber";
import CardExpiry from "components/v3/credit-card/expiry/CardExpiry";
import CardCVC from "components/v3/credit-card/cvc/CardCVC";
import GoogleTracking from "services/GoogleTracking";
import ErrorHandler from "components/v3/error-handler/ErrorHandler";
import SubmitError from "components/v3/submit-error/SubmitError";
import ExistingPayment from "./existing-payment/ExistingPayment";
import PayPalButton from "components/v3/paypal-button/PayPalButton";
import { PAYMENT_METHODS, THEMES } from "common/constants/Constants";
import { isIE, isEdge } from "utils/isBrowser";
import stripeStyles from "./stripeStyles";
import paypalStyles from "./paypalStyles";
import styles from "./styles.module.scss";
import isPaymentValidated from "utils/isPaymentValidated";
import { pricingStrategy, determineProductDisplayName } from "components/v3/subscription-details/util";

class PaymentForm extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            stripePaymentProvider: false
        };

        this._onStripeError = this._onStripeError.bind(this);
        this._onPaypalSubmit = this._onPaypalSubmit.bind(this);
        this._onPaypalClick = this._onPaypalClick.bind(this);
        this._onApplePayClick = this._onApplePayClick.bind(this);
        this._onGooglePayClick = this._onGooglePayClick.bind(this);
        this._onPaymentMethodReset = this._onPaymentMethodReset.bind(this);
        this._setPaymentMethodToStripeProvider = this._setPaymentMethodToStripeProvider.bind(this);

        this._setupPaymentRequest();
    }

    componentDidMount() {
        this.props.setStripeProvider(this.props.stripe);
    }

    _setPaymentMethodToStripeProvider() {
        const { setPaymentMethod, stripePaymentProvider } = this.props;

        setPaymentMethod(stripePaymentProvider);
    }

    _setupPaymentRequest() {
        const {
            stripe,
            productName: checkoutProductName,
            totalPrice: checkoutTotalPrice,
            subscription: {
                metadata = {},
                ratePlan: {
                    offerType
                } = {},
                type
            } = {},
            country
        } = this.props;

        const accountName = metadata?.productType;

        const {
            nextChargePrice: changePaymentTotalPrice
        } = pricingStrategy(metadata);

        const changePaymentProductName = determineProductDisplayName(type, accountName, null, offerType);

        // checkout* are used when checking out, changePayment* are used when changing your payment
        const productName = checkoutProductName || changePaymentProductName;
        const totalPrice = checkoutTotalPrice || changePaymentTotalPrice;

        try {
            // For full documentation of the available paymentRequest options, see:
            // https://stripe.com/docs/stripe.js#the-payment-request-object

            this.paymentRequest = stripe.paymentRequest({
                country,
                currency: "usd",
                total: {
                    label: `Bloomberg.com: ${ productName }`,
                    amount: Math.floor(totalPrice * 100) // in cents
                },
                requestPayerName: true,
                requestPayerEmail: true
            });

            this.paymentRequest.on("token", ({ token, complete }) => {
                // use a function here to ensure that stripePaymentProvider is its current value
                this._setPaymentMethodToStripeProvider();
                this.props.onPaymentRequestSubmit(token, complete);
            });

            this.paymentRequest.canMakePayment().then((result) => {
                this.props.setStripePaymentProvider(getStripePaymentProvider(result));
            });
        } catch (error) {
            console.log(error);
        }
    }

    _onPaypalSubmit(paypalInfo) {
        this.props.onPayPalSubmit(paypalInfo);
    }

    _onPaypalClick() {
        GoogleTracking.registerSubscriptionEvent("click", "payment:paypal");
    }

    _onApplePayClick() {
        GoogleTracking.registerSubscriptionEvent("click", "payment:applepay");

        this.paymentRequest.show();
    }

    _onGooglePayClick() {
        GoogleTracking.registerSubscriptionEvent("click", "payment:googlepay");

        this.paymentRequest.show();
    }

    _onPaymentMethodReset() {
        this.props.onChangePaymentMethod(PAYMENT_METHODS.STRIPE);
    }

    _onStripeError(source, error) {
        this.props.onStripeError(source, error);
    }

    render() {
        const {
            paypalId,
            cardInformation,
            errors,
            title,
            showExistingPayment = true,
            showErrors,
            showLoading
        } = this.props;

        const content = showExistingPayment && isPaymentValidated(cardInformation, paypalId) && !showLoading
            ? <ExistingPaymentMethod { ...this.props } onChangePaymentMethod={ this._onPaymentMethodReset } />
            : (
                <EnterPayment
                    { ...this.props }
                    onPaypalClick={ this._onPaypalClick }
                    onApplePayClick={ this._onApplePayClick }
                    onGooglePayClick={ this._onGooglePayClick }
                    paymentRequest={ this.paymentRequest }
                />
            );

        return (
            <div className={ styles.paymentForm }>
                { showErrors && (
                    <ErrorHandler
                        types={ ["creditCard", "submitPayment", "applePay", "googlePay", "unknown"] }
                        errors={ errors }
                    >
                        <SubmitError theme={ THEMES.ERROR_THEME_PAGE_LEVEL } />
                    </ErrorHandler>
                ) }
                { title && (
                    <h1 className={ styles.header }>
                        { title }
                    </h1>
                ) }
                { content }
            </div>
        );
    }
}

export const ExistingPaymentMethod = (props) => {
    return (
        <div className={ styles.existingPaymentMethod }>
            <ExistingPayment { ...props } onChangePaymentMethod={ props.onChangePaymentMethod } />
        </div>
    );
};

export const EnterPayment = (props) => {
    const {
        onStripeError,
        onPaypalSubmit,
        onPaypalClick,
        onApplePayClick,
        onGooglePayClick,
        paypalEnv,
        productType,
        stripePaymentProvider,
        errors,
        showPaymentButtons,
        paymentRequest,
        showLoading
    } = props;

    return (
        <div className={ styles.enterPayment }>
            { showPaymentButtons && (
                <PaymentButtons
                    onPaypalSubmit={ onPaypalSubmit }
                    onPaypalClick={ onPaypalClick }
                    onApplePayClick={ onApplePayClick }
                    onGooglePayClick={ onGooglePayClick }
                    paypalEnv={ paypalEnv }
                    productType={ productType }
                    stripePaymentProvider={ stripePaymentProvider }
                    paymentRequest={ paymentRequest }
                />
            ) }
            <div className={ styles.creditCard }>
                { showLoading && (
                    <div className={ styles.thinking }>
                        <span className={ styles.dot } />
                        <span className={ styles.dot } />
                        <span className={ styles.dot } />
                    </div>
                ) }
                <CardInformation
                    onStripeError={ onStripeError }
                    errors={ errors }
                />
            </div>
        </div>
    );
};

const PaymentButtons = (props) => {
    const {
        paypalEnv,
        onPaypalSubmit,
        onPaypalClick,
        onApplePayClick,
        onGooglePayClick,
        productType,
        stripePaymentProvider,
        paymentRequest
    } = props;

    // [LW] Don't render PayPal if IE/Edge, as current paypal-checkout version breaks
    const isIEOrEdge = isIE() || isEdge();

    return (
        <div className={ styles.paymentButtons }>
            <PayPalButtonTab
                enabled={ !isIEOrEdge }
                env={ paypalEnv }
                onSubmit={ onPaypalSubmit }
                onClick={ onPaypalClick }
                productType={ productType }
            />
            <ApplePayButtonTab
                enabled={ stripePaymentProvider === PAYMENT_METHODS.APPLE }
                onClick={ onApplePayClick }
            />
            <GooglePayButtonTab
                enabled={ !isIEOrEdge && stripePaymentProvider === PAYMENT_METHODS.GOOGLE }
                onClick={ onGooglePayClick }
                paymentRequest={ paymentRequest }
            />
        </div>
    );
};

const CardInformation = ({ onStripeError, errors }) => {
    return (
        <div className={ styles.cardInfo }>
            <ErrorHandler type="creditCard" errors={ errors }>
                <CardNumber onStripeError={ onStripeError } styles={ stripeStyles } />
            </ErrorHandler>
            <div className={ styles.expiryAndCVC }>
                <CardExpiry onStripeError={ onStripeError } />
                <CardCVC onStripeError={ onStripeError } />
            </div>
        </div>
    );
};

const PayPalButtonTab = ({ enabled, env, onSubmit, onClick, productType }) => {
    if (!enabled) {
        return null;
    }

    const className = `${ styles.paymentButton } ${ styles.paypalButton }`;

    return (
        <div className={ className }>
            <PayPalButton
                env={ env }
                style={ paypalStyles }
                onSubmit={ onSubmit }
                onClick={ onClick }
                productType={ productType }
            />
        </div>
    );
};

const ApplePayButtonTab = ({ enabled, onClick }) => {
    if (!enabled) {
        return null;
    }

    const className = `${ styles.paymentButton } ${ styles.applePayButton } apple-pay-button apple-pay-button-black`;

    return (
        <button className={ className } type="button" onClick={ onClick } />
    );
};

const GooglePayButtonTab = ({ enabled, onClick }) => {
    if (!enabled) {
        return null;
    }

    const className = `${ styles.paymentButton } ${ styles.googlePayButton }`;

    return (
        <button type="button" className={ className } onClick={ onClick } />
    );
};

function getStripePaymentProvider(result) {
    if (!result) {
        return null;
    }

    if (result && result.applePay) {
        return PAYMENT_METHODS.APPLE;
    }

    return PAYMENT_METHODS.GOOGLE;
}

PaymentForm.propTypes = {
    paymentMethod: PropTypes.string,
    onPaymentRequestSubmit: PropTypes.func.isRequired,
    onPaypalSubmit: PropTypes.func.isRequired,
    paypalEnv: PropTypes.string.isRequired,
    productType: PropTypes.string,
    paypalId: PropTypes.string,
    cardInformation: PropTypes.object,
    errors: PropTypes.object,
    showErrors: PropTypes.bool,
    stripePaymentProvider: PropTypes.string,
    showLoading: PropTypes.bool
};

export default injectStripe(PaymentForm);
